import React from 'react'

import dynamic from 'next/dynamic'
import { Box, Chip, Paper, Typography } from '@mui/material'
import type { ChartsAxisContentProps } from '@mui/x-charts/ChartsTooltip'

import * as COLORS from '@/app/theme/colors/flyin'

const WeatherChart = dynamic(() => import('./weatherChart'), { ssr: false })

interface ChartDatasetRecord {
    [key: string]: string | number | Date | null | undefined
}

export interface ChartDataItem extends ChartDatasetRecord {
    high: number
    low: number
    rainfall: number
    month: string
}

interface BestTimeToVisitProps {
    title: string
    summary: string
    bestMonths: string[]
    chartDataset: ChartDataItem[]
    tempMin: number
    tempMax: number
    TooltipSlot: React.ElementType<ChartsAxisContentProps>
}

interface WeatherTooltipContentProps {
    month: string
    high: number
    low: number
    rain: number
    condition: string
    note: string
}

const TOOLTIP_ROWS = (high: number, low: number, rain: number) => [
    { color: COLORS.CHART_TEMP_HIGH, label: 'High', value: `${high}°C` },
    { color: COLORS.CHART_TEMP_LOW, label: 'Low', value: `${low}°C` },
    { color: COLORS.CHART_RAINFALL, label: 'Rain', value: `${rain} mm` },
]

export const WeatherTooltipContent: React.FC<WeatherTooltipContentProps> = ({ month, high, low, rain, condition, note }) => (
    <Paper elevation={3} sx={{ p: 2, minWidth: 220, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.9rem' }}>
            {month}
        </Typography>
        {condition && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                {condition}
            </Typography>
        )}
        {TOOLTIP_ROWS(high, low, rain).map(row => (
            <Box
                key={row.label}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.625 }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.color, flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, ml: 4 }}>{row.value}</Typography>
            </Box>
        ))}
        {note && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                {note}
            </Typography>
        )}
    </Paper>
)

const WeatherDataTable: React.FC<{ chartDataset: ChartDataItem[] }> = ({ chartDataset }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
            component="table"
            aria-label="Monthly weather data"
            sx={{
                width: '100%',
                borderCollapse: 'collapse',
                '& th, & td': {
                    px: 1.5,
                    py: 1,
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                },
                '& th': {
                    fontWeight: 600,
                    color: 'text.secondary',
                    bgcolor: 'grey.50',
                    fontSize: '0.75rem',
                },
                '& tbody tr:last-child td': {
                    borderBottom: 'none',
                },
            }}
        >
            <thead>
                <tr>
                    <th>Month</th>
                    <th>High (&deg;C)</th>
                    <th>Low (&deg;C)</th>
                    <th>Rainfall (mm)</th>
                </tr>
            </thead>
            <tbody>
                {chartDataset.map(row => (
                    <tr key={row.month}>
                        <td>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                {row.month}
                            </Typography>
                        </td>
                        <td>
                            <Typography variant="body2" sx={{ color: COLORS.CHART_TEMP_HIGH, fontWeight: 600, fontSize: '0.8rem' }}>
                                {row.high}°
                            </Typography>
                        </td>
                        <td>
                            <Typography variant="body2" sx={{ color: COLORS.CHART_TEMP_LOW, fontWeight: 600, fontSize: '0.8rem' }}>
                                {row.low}°
                            </Typography>
                        </td>
                        <td>
                            <Typography variant="body2" sx={{ color: COLORS.CHART_RAINFALL, fontWeight: 600, fontSize: '0.8rem' }}>
                                {row.rainfall}
                            </Typography>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Box>
    </Paper>
)

const BestTimeToVisit: React.FC<BestTimeToVisitProps> = ({ title, summary, bestMonths, chartDataset, tempMin, tempMax, TooltipSlot }) => {
    const [chartLoaded, setChartLoaded] = React.useState(false)

    return (
        <Box component="section" sx={{ mb: 3 }}>
            {title && (
                <Typography
                    component="h2"
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.1rem' }, mb: 1.5 }}
                >
                    {title}
                </Typography>
            )}
            {summary && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
                    {summary}
                </Typography>
            )}
            {bestMonths.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {bestMonths.map(month => (
                        <Chip key={month} size="small" label={month} sx={{ bgcolor: 'grey.100', fontSize: '0.75rem' }} />
                    ))}
                </Box>
            )}
            {chartDataset.length > 0 && (
                <>
                    {!chartLoaded && <WeatherDataTable chartDataset={chartDataset} />}
                    <WeatherChart
                        chartDataset={chartDataset}
                        tempMin={tempMin}
                        tempMax={tempMax}
                        TooltipSlot={TooltipSlot}
                        onReady={() => setChartLoaded(true)}
                    />
                </>
            )}
        </Box>
    )
}

export default BestTimeToVisit
export type { BestTimeToVisitProps, WeatherTooltipContentProps }
